import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Copy, Link2, Check, Trash2, X, MapPin, Users, Calendar, Ban, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function formatDateLong(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EventDetailDialog({ open, onClose, eventId, onChanged }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await api.get(`/events/${eventId}`);
      setEvent(res.data);
    } catch (err) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!event && !loading) return null;

  const publicLink = event ? `${window.location.origin}/event/${event.public_id}` : "";

  const tallies = event
    ? event.date_options.map((_, i) => event.votes.filter((v) => v.date_index === i).length)
    : [];

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
      toast.success("Copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const shareNative = async () => {
    const text = `⚡ Ti hanno invitato a "${event.title}"\nVota la data: ${publicLink}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `ZAP — ${event.title}`,
          text,
          url: publicLink,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          toast.error("Condivisione non riuscita");
        }
      }
    } else {
      // Desktop fallback: copy link
      await copy(publicLink, "link");
      toast("Su questo dispositivo non c'è il selettore di condivisione: link copiato, incollalo nel gruppo WhatsApp.");
    }
  };

  const confirmDate = async (idx) => {
    if (!window.confirm(`Confermare la data ${idx + 1} come definitiva?`)) return;
    setConfirming(idx);
    try {
      await api.post(`/events/${event.id}/confirm`, { final_date_index: idx });
      toast.success("Data confermata. Tutti gli invitati saranno informati.");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setConfirming(null);
    }
  };

  const cancelEvent = async () => {
    if (!window.confirm("Annullare questo ritrovo?")) return;
    setCancelling(true);
    try {
      await api.post(`/events/${event.id}/cancel`);
      toast.success("Ritrovo annullato");
      await load();
      onChanged?.();
    } catch {
      toast.error("Errore");
    } finally {
      setCancelling(false);
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${event.id}`);
      toast.success("Ritrovo eliminato");
      onChanged?.();
      onClose();
    } catch {
      toast.error("Errore");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="event-detail-dialog"
        className="max-w-2xl rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto"
      >
        {loading || !event ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF7C43]" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full bg-red-50 px-3 py-1">
                <span className={`h-1.5 w-1.5 rounded-full ${event.status === "confirmed" ? "bg-green-500" : event.status === "cancelled" ? "bg-stone-400" : "bg-red-500 pulse-dot"}`} />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-red-700">
                  {event.status === "confirmed" ? "Confermato" : event.status === "cancelled" ? "Annullato" : "In votazione"}
                </span>
              </div>
              <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-[#2D2926]">
                {event.title}
              </DialogTitle>
              {event.description && (
                <DialogDescription className="text-[#5C5855]">
                  {event.description}
                </DialogDescription>
              )}
            </DialogHeader>

            {event.location && (
              <div className="flex items-center gap-2 text-sm text-[#5C5855]">
                <MapPin className="h-3.5 w-3.5 text-[#C87941]" />
                {event.location}
              </div>
            )}

            {/* Confirmed banner */}
            {event.status === "confirmed" && event.final_date && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-green-700">Data confermata</p>
                <p className="mt-1 font-display text-xl font-bold text-green-900">
                  {formatDateLong(event.final_date)}
                </p>
              </div>
            )}

            {/* Share link */}
            {event.status === "voting" && (
              <div className="rounded-2xl border border-[#E8E1D9] bg-[#FBF9F6] p-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                  Condividi con gli invitati
                </p>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="event-share-link"
                    readOnly
                    value={publicLink}
                    className="flex-1 truncate rounded-lg border border-[#E8E1D9] bg-white px-3 py-2 font-mono text-xs text-[#5C5855]"
                  />
                  <button
                    data-testid="event-copy-link"
                    onClick={() => copy(publicLink, "link")}
                    title="Copia link"
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF7C43] text-white hover:bg-[#E66A35]"
                  >
                    {copied === "link" ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                  </button>
                </div>

                {/* Share actions */}
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    data-testid="event-share-native"
                    onClick={shareNative}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-[#2D2926] px-3 py-2.5 text-xs font-semibold text-white hover:bg-[#FF7C43]"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Condividi (anche gruppi)
                  </button>
                  <a
                    data-testid="event-share-whatsapp"
                    href={`https://wa.me/?text=${encodeURIComponent(`⚡ Ti hanno invitato a "${event.title}"\nVota la data: ${publicLink}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-green-700"
                  >
                    WhatsApp chat
                  </a>
                </div>
                <p className="mt-2 text-[10px] uppercase tracking-wider text-[#C87941]/70">
                  Codice: <code className="font-bono font-bold text-[#FF7C43]">{event.public_id}</code>
                </p>
              </div>
            )}

            {/* Dates with votes */}
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#FF7C43]" />
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                  Date proposte ({event.votes.length} voti totali)
                </p>
              </div>
              <div className="space-y-2">
                {event.date_options.map((d, i) => {
                  const count = tallies[i] || 0;
                  const isWinner = event.final_date === d;
                  const max = Math.max(...tallies, 1);
                  const pct = (count / max) * 100;
                  return (
                    <div
                      key={i}
                      data-testid={`event-date-row-${i}`}
                      className={`rounded-2xl border p-3 transition ${
                        isWinner ? "border-green-400 bg-green-50" : "border-[#E8E1D9] bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#2D2926]">
                            {formatDateLong(d)}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E8E1D9]">
                              <div
                                className="h-full bg-[#FF7C43] transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="flex items-center gap-1 text-xs font-bold text-[#FF7C43]">
                              <Users className="h-3 w-3" />
                              {count}
                            </span>
                          </div>
                        </div>
                        {event.status === "voting" && (
                          <Button
                            data-testid={`event-confirm-${i}`}
                            type="button"
                            size="sm"
                            onClick={() => confirmDate(i)}
                            disabled={confirming !== null}
                            className="shrink-0 rounded-full bg-red-600 text-white hover:bg-red-700"
                          >
                            {confirming === i ? <Loader2 className="h-3 w-3 animate-spin" /> : "Conferma"}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Voters list */}
            {event.votes.length > 0 && (
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                  Chi ha votato
                </p>
                <div className="space-y-1.5">
                  {event.votes.map((v) => (
                    <div key={v.id} className="flex items-start justify-between gap-2 rounded-xl border border-[#E8E1D9] bg-white px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#2D2926]">{v.voter_name}</p>
                        {v.note && <p className="text-xs text-[#5C5855]">{v.note}</p>}
                      </div>
                      <span className="shrink-0 rounded-full bg-[#FBF9F6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C87941]">
                        Data {v.date_index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="flex flex-row items-center justify-between gap-2 border-t border-[#E8E1D9] pt-4 sm:justify-between">
              <div className="flex gap-2">
                {event.status === "voting" && (
                  <Button
                    data-testid="event-cancel-button"
                    type="button"
                    variant="outline"
                    onClick={cancelEvent}
                    disabled={cancelling}
                    className="rounded-full border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="mr-1 h-3.5 w-3.5" />}
                    Annulla evento
                  </Button>
                )}
                <Button
                  data-testid="event-delete-button"
                  type="button"
                  variant="ghost"
                  onClick={deleteEvent}
                  disabled={deleting}
                  className="rounded-full text-red-600 hover:bg-red-50"
                >
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-full border-[#E8E1D9]"
              >
                <X className="mr-1 h-4 w-4" /> Chiudi
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
